import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Lock, Play, RotateCcw, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { KindIcon } from './QualityBadges';
import { KIND_META, scoreTone } from './qualityConfig';
import { withAssignment } from './assigned';

const VIOLET = 'shrink-0 bg-violet-600 text-white hover:bg-violet-700';

const itemNote = (item, done, tried, canSubmit, required) => {
    if (done) return ' · submitted';
    if (canSubmit) return ' · ready to submit';
    if (tried) return ` · need ${required}% · retake`;
    return item.started_at ? ' · started' : '';
};

export const TaskItemRow = ({ taskId, item, locked, requiredScore = 70, onChanged }) => {
    const navigate = useNavigate();
    const { api } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const done = item.status === 'completed';
    const tried = item.best_score !== null && item.best_score !== undefined;
    const canSubmit = !done && tried && item.best_score >= requiredScore;
    const mustRetake = tried && !done && !canSubmit;
    const Icon = locked ? Lock : tried || done ? RotateCcw : Play;
    const start = () => {
        api.post(`/quality-tasks/my/${taskId}/items/${item.item_id}/start`).catch(() => {});
        navigate(withAssignment(item.link, taskId));
    };
    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post(`/quality-tasks/my/${taskId}/items/${item.item_id}/submit`);
            window.dispatchEvent(new Event('quality-tasks-updated'));
            toast.success(`Submitted · best score ${item.best_score}%`);
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not submit. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <li className="flex items-center gap-3 rounded-xl border bg-background/60 px-3 py-2.5 transition-colors hover:bg-muted/40" data-testid={`quality-task-item-${item.item_id}`}>
            <KindIcon kind={item.kind} />
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{item.title}</p>
                <p className={`truncate text-xs ${mustRetake ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`} data-testid={`quality-task-item-note-${item.item_id}`}>
                    {KIND_META[item.kind].short} · {item.group}{itemNote(item, done, tried, canSubmit, requiredScore)}
                </p>
            </div>
            {tried && (
                <div className="shrink-0 text-right" data-testid={`quality-task-item-score-${item.item_id}`}>
                    <p className={`font-heading text-lg font-bold leading-none tabular-nums ${scoreTone(item.best_score)}`}>{item.best_score}</p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">best · {item.attempts} {item.attempts === 1 ? 'try' : 'tries'}</p>
                </div>
            )}
            <Button size="sm" variant={done || canSubmit || locked ? 'outline' : 'default'} disabled={locked || submitting} onClick={start}
                className={done || canSubmit || locked ? 'shrink-0' : VIOLET} data-testid={`quality-task-start-${item.item_id}`}>
                <Icon className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">{locked ? 'Locked' : tried || done ? 'Retake' : 'Start'}</span>
            </Button>
            {canSubmit && !locked && (
                <Button size="sm" onClick={submit} disabled={submitting} className={VIOLET} data-testid={`quality-task-submit-${item.item_id}`}>
                    {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin sm:mr-1.5" /> : <Send className="h-3.5 w-3.5 sm:mr-1.5" />}
                    <span className="hidden sm:inline">Submit</span>
                </Button>
            )}
        </li>
    );
};
